/**
 * Shared value formatting for the price chart.
 *
 * The tooltip, the hover header and the value footer all render from
 * buildValueReadout, so the three can't drift apart. Formatting rules are
 * lifted verbatim from the tooltip that used to own them: prices to 2dp with a
 * $, MACD/AC to 3dp, RSI to 1dp, volume comma-grouped.
 */

const fmtPrice = (v) => `$${Number(v).toFixed(2)}`;

/**
 * Ordered value entries for one chart datum.
 *
 * Entries always include an entry per ACTIVE indicator, rendering '—' when that
 * bar has no value — so a strip built from this never changes its item count as
 * the cursor moves.
 *
 * Each entry carries EITHER `className` (Tailwind, matching the tooltip's
 * hardcoded colors) OR `color` (resolved from the indicator's own palette).
 *
 * @param {Object|null} point - a chartData datum
 * @param {Object} opts
 * @param {Object} opts.flags - { hasBollinger, showBollinger, hasMACD, hasRSI, hasSMA50,
 *                               hasSMA200, hasSMA720, hasEMA, hasVWAP, hasVolume, hasAC }
 * @param {Function} opts.getIndicatorColor - (indicator, colorKey) => string
 * @param {boolean} [opts.includeVolume=true]
 * @returns {Array<{key:string,label:string,value:string,className?:string,color?:string}>}
 */
export function buildValueReadout(point, opts = {}) {
  const { flags = {}, getIndicatorColor = () => undefined, includeVolume = true } = opts;
  const d = point || {};
  const entries = [];

  const push = (key, label, raw, format, style) => {
    entries.push({
      key,
      label,
      value: raw == null || Number.isNaN(Number(raw)) ? '—' : format(raw),
      ...style,
    });
  };

  // Price — always shown
  push('price', 'Price', d.close, fmtPrice, { className: 'text-green-400' });

  if (flags.showBollinger && flags.hasBollinger) {
    entries.push({
      key: 'bb',
      label: 'BB',
      value: d.bb_upper == null || d.bb_lower == null
        ? '—'
        : `${fmtPrice(d.bb_lower)}-${fmtPrice(d.bb_upper)}`,
      className: 'text-blue-400',
    });
  }

  if (flags.hasSMA50) push('sma_50', 'SMA50', d.sma_50, fmtPrice, { color: getIndicatorColor('sma_50', 'color') });
  if (flags.hasSMA200) push('sma_200', 'SMA200', d.sma_200, fmtPrice, { color: getIndicatorColor('sma_200', 'color') });
  if (flags.hasSMA720) push('sma_720', 'SMA720', d.sma_720, fmtPrice, { color: getIndicatorColor('sma_720', 'color') });
  if (flags.hasEMA) push('ema', 'EMA', d.ema, fmtPrice, { color: getIndicatorColor('ema', 'color') });
  if (flags.hasVWAP) push('vwap', 'VWAP', d.vwap, fmtPrice, { color: getIndicatorColor('vwap', 'color') });

  if (flags.hasMACD) {
    const to3 = (v) => Number(v).toFixed(3);
    push('macd', 'MACD', d.macd, to3, { className: 'text-cyan-400' });
    push('macd_signal', 'Signal', d.macd_signal, to3, { className: 'text-orange-400' });
    push('macd_histogram', 'Hist', d.macd_histogram, to3, { className: 'text-gray-400' });
  }

  if (flags.hasRSI) {
    push('rsi', 'RSI', d.rsi, (v) => Number(v).toFixed(1), { className: 'text-purple-400' });
  }

  if (includeVolume && flags.hasVolume) {
    push('volume', 'Vol', d.volume, (v) => Number(v).toLocaleString(), { className: 'text-gray-500' });
  }

  if (flags.hasAC) {
    push('ac', 'AC', d.ac, (v) => Number(v).toFixed(3), {
      color: getIndicatorColor('ac', Number(d.ac) >= 0 ? 'bullish' : 'bearish'),
    });
  }

  return entries;
}

/**
 * Human label for a bar. Daily output is character-identical to what the
 * tooltip produced before this helper existed.
 *
 * Parsed by string slicing rather than `new Date()` — the date strings are
 * market-local wall clock, and Date would shift them by the viewer's offset.
 *
 * @param {string} dateStr - 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:MM:SS'
 * @param {string} [interval='day']
 */
export function formatBarLabel(dateStr, interval = 'day') {
  if (!dateStr || typeof dateStr !== 'string') return 'Unknown date';
  const [datePart, timePart] = dateStr.split('T');
  if (!datePart.includes('-')) return dateStr;
  const [y, m, d] = datePart.split('-');

  if (interval === 'day' || !timePart) {
    return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
  }

  const [hh, mm] = timePart.split(':');
  const hour = parseInt(hh, 10);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${parseInt(m, 10)}/${parseInt(d, 10)} ${hour12}:${mm} ${suffix}`;
}

/**
 * X-axis tick label. Daily output ('M/D') is identical to the formatter this
 * replaces, so every non-intraday chart is unchanged.
 */
export function formatAxisTick(dateStr, interval = 'day') {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const [datePart, timePart] = dateStr.split('T');
  const [, m, d] = datePart.split('-');

  if (interval === 'day' || !timePart) {
    return `${parseInt(m, 10)}/${parseInt(d, 10)}`;
  }
  if (interval === '30min') {
    // A week of 30-min bars: label the day, not the time.
    return `${parseInt(m, 10)}/${parseInt(d, 10)}`;
  }
  const [hh, mm] = timePart.split(':');
  const hour = parseInt(hh, 10);
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${mm}`;
}

/**
 * Explicit tick values for intraday axes.
 *
 * Returns undefined for daily so Recharts keeps its own tick placement — that
 * is what leaves every existing chart untouched. Intraday needs explicit ticks
 * because otherwise a session of 5-minute bars renders the same 'M/D' label
 * dozens of times.
 *
 * @param {Array} chartData
 * @param {string} interval - 'day' | '5min' | '30min'
 * @param {number} [maxTicks=8]
 * @returns {Array<string>|undefined}
 */
export function computeAxisTicks(chartData, interval, maxTicks = 8) {
  if (interval === 'day' || !Array.isArray(chartData) || chartData.length === 0) {
    return undefined;
  }

  let candidates;
  if (interval === '30min') {
    // One tick per session, at its first bar.
    const seen = new Set();
    candidates = chartData.filter((row) => {
      const day = String(row?.date || '').split('T')[0];
      if (!day || seen.has(day)) return false;
      seen.add(day);
      return true;
    });
  } else {
    // Clean hour / half-hour boundaries.
    candidates = chartData.filter((row) => {
      const time = String(row?.date || '').split('T')[1];
      if (!time) return false;
      const minutes = time.slice(3, 5);
      return minutes === '00' || minutes === '30';
    });
  }

  if (candidates.length === 0) return undefined;

  const step = Math.ceil(candidates.length / maxTicks);
  return candidates
    .filter((_, i) => i % step === 0)
    .map((row) => row.date);
}
